/**
 * Per-product commitment-config status, derived from the active config's
 * early-termination retention fraction returned by
 * GET /admin/commitment-configs/active (the panel's own read — the product
 * listings no longer carry the commitment retention; audit #138).
 *
 * The DB column `early_termination_retention_pct` is `numeric(5,4) DEFAULT 0
 * NOT NULL` on every published config and the endpoint returns the active row
 * (`effective_to IS NULL`) per product, so the value is unambiguous:
 *   · null → no active config at all (agents get a 404 drafting commitments)
 *   · 0    → a config exists but carries no early-exit penalty
 *   · > 0  → fully configured
 *
 * Treating 0% as "needs attention" is a deliberate, advisory choice for the
 * illiquid PE/RE launch (early-exit penalties are expected on every product).
 * It never blocks publishing — it only flags products for the admin to review.
 */
export type ConfigStatusKey = 'missing_config' | 'missing_penalty' | 'configured';

export interface ProductConfigStatus {
  key: ConfigStatusKey;
  /** Short badge label (English UI copy). */
  label: string;
  /** True when a super_admin should still act on this product. */
  needsAttention: boolean;
}

export function productConfigStatus(retentionPct: string | null): ProductConfigStatus {
  if (retentionPct === null) {
    return { key: 'missing_config', label: 'No active config', needsAttention: true };
  }
  const fraction = Number.parseFloat(retentionPct);
  // Fail safe: a malformed value flags for attention rather than reading as OK.
  if (Number.isNaN(fraction) || fraction <= 0) {
    return { key: 'missing_penalty', label: 'No early-exit penalty (0%)', needsAttention: true };
  }
  return {
    key: 'configured',
    label: `Penalty ${(fraction * 100).toFixed(2)}%`,
    needsAttention: false,
  };
}

/** How many of the given products still need config attention. Accepts the
 *  minimal structural shape so it stays decoupled from the full ProductSummary. */
export function countNeedingAttention(
  products: readonly { early_termination_retention_pct: string | null }[],
): number {
  return products.reduce(
    (n, p) => (productConfigStatus(p.early_termination_retention_pct).needsAttention ? n + 1 : n),
    0,
  );
}

function isDigits(text: string): boolean {
  if (text.length === 0) return false;
  for (const ch of text) {
    if (ch < '0' || ch > '9') return false;
  }
  return true;
}

function groupThousands(digits: string): string {
  const groups: string[] = [];
  for (let end = digits.length; end > 0; end -= 3) {
    groups.push(digits.slice(Math.max(0, end - 3), end));
  }
  groups.reverse();
  return groups.join(',');
}

function trimTrailingZeros(text: string): string {
  let end = text.length;
  while (end > 0 && text[end - 1] === '0') end -= 1;
  return text.slice(0, end);
}

/**
 * An amount as a person reads it: thousands grouped, two decimals, and more only when the
 * stored value really carries them. The API returns Decimal strings with eight decimals
 * ("2500.00000000"); string work only, so no float ever touches a money value.
 */
export function formatAmount(value: string | null): string {
  if (value === null) return '';
  const negative = value.startsWith('-');
  const [whole, fraction = '', ...rest] = (negative ? value.slice(1) : value).split('.');
  const wellFormed = rest.length === 0 && isDigits(whole) && (fraction === '' ? !value.endsWith('.') : isDigits(fraction));
  if (!wellFormed) return value;
  const trimmed = trimTrailingZeros(fraction);
  const decimals = trimmed.length > 2 ? trimmed : trimmed.padEnd(2, '0');
  return `${negative ? '-' : ''}${groupThousands(whole)}.${decimals}`;
}

/** A retention fraction ("0.0500") as a percentage; a missing or malformed value as a dash. */
export function formatRetentionPct(fraction: string | null | undefined): string {
  const value = fraction === null || fraction === undefined ? Number.NaN : Number.parseFloat(fraction);
  return Number.isNaN(value) ? '—' : `${(value * 100).toFixed(2)}%`;
}
